/**
 * Proof video capture flow (spec §9.5), inside a bottom sheet:
 *
 *   consent → capture → preview/retake → private upload → done
 *
 * - getUserMedia + MediaRecorder when supported (feature-detected), with a
 *   60-second hard auto-stop;
 * - <input type="file" accept="video/*" capture> fallback, validated against
 *   the 60 s / 50 MB limits with clear Dutch feedback;
 * - consent copy and explicit opt-in BEFORE any recording;
 * - preview + retake before upload;
 * - upload via repositories.medication.attachProof with visible progress
 *   states; private storage, never a public URL;
 * - the video is user-submitted evidence, never clinical verification.
 *
 * While PROOF_VIDEO_ENABLED is false (spec §9.6 privacy gate) the sheet shows
 * the complete admin checklist with status 'nog niet geactiveerd' instead of
 * the recording flow. The flow itself is fully implemented behind the flag.
 */
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Banner, BottomSheet, Button, Progress, Spinner } from '@/components/ui'
import { CameraIcon, CheckIcon, RefreshIcon, UploadIcon, VideoIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import type { MedicationIntakeEvent } from '@/types'
import { PROOF_VIDEO_ENABLED } from '../flags'
import { errorMessage } from '../useMedicationData'
import { ProofConsent } from './ProofConsent'
import { ProofPrivacyGatePanel } from './ProofPrivacyGatePanel'
import {
  PROOF_MAX_DURATION_SECONDS,
  PROOF_VALIDATION_COPY,
  formatBytes,
  formatDuration,
  validateProofFile,
  type ProofValidationError,
} from './limits'
import {
  recordingToFile,
  recorderPhaseMessage,
  useProofRecorder,
} from './useProofRecorder'
import '../medication.css'

export type ProofCaptureSheetProps = {
  open: boolean
  onClose: () => void
  intakeEventId: string
  /** Short Dutch label of the medication moment, for context copy. */
  contextLabel?: string
  onUploaded: (event: MedicationIntakeEvent) => void
}

type CaptureStep = 'consent' | 'capture' | 'preview' | 'uploading' | 'done'

type FilePreview = {
  file: File
  url: string
  sizeLabel: string
}

export function ProofCaptureSheet({ open, onClose, intakeEventId, contextLabel, onUploaded }: ProofCaptureSheetProps) {
  const [step, setStep] = useState<CaptureStep>('consent')
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null)
  const [validationError, setValidationError] = useState<ProofValidationError | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [validatingFile, setValidatingFile] = useState(false)

  const recorder = useProofRecorder()
  const liveVideoRef = useRef<HTMLVideoElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const filePreviewUrlRef = useRef<string | null>(null)

  // Reset the whole flow whenever the sheet is (re)opened; releasing the
  // camera stream also happens here so closing the sheet mid-recording
  // never leaves the camera on.
  useEffect(() => {
    // Always release the camera and any object URL when closing; on open
    // the flow restarts at the consent step.
    recorder.retake()
    if (filePreviewUrlRef.current) {
      URL.revokeObjectURL(filePreviewUrlRef.current)
      filePreviewUrlRef.current = null
    }
    setFilePreview(null)
    if (open) {
      setStep('consent')
      setValidationError(null)
      setUploadError(null)
      setValidatingFile(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Attach the live camera stream to the preview <video>.
  useEffect(() => {
    const video = liveVideoRef.current
    if (video && recorder.stream && (recorder.phase === 'ready' || recorder.phase === 'recording')) {
      if (video.srcObject !== recorder.stream) {
        video.srcObject = recorder.stream
        void video.play().catch(() => undefined)
      }
    }
  }, [recorder.stream, recorder.phase, step])

  // A finished in-app recording moves the flow to the preview step.
  useEffect(() => {
    if (recorder.phase === 'recorded' && recorder.recording && step === 'capture') {
      setStep('preview')
    }
  }, [recorder.phase, recorder.recording, step])

  const clearFilePreview = useCallback(() => {
    if (filePreviewUrlRef.current) {
      URL.revokeObjectURL(filePreviewUrlRef.current)
      filePreviewUrlRef.current = null
    }
    setFilePreview(null)
  }, [])

  const handleFileChosen = useCallback(
    async (file: File | null) => {
      if (!file) return
      setValidationError(null)
      setValidatingFile(true)
      try {
        const problem = await validateProofFile(file, { checkDuration: true })
        if (problem) {
          setValidationError(problem)
          return
        }
        clearFilePreview()
        const url = URL.createObjectURL(file)
        filePreviewUrlRef.current = url
        setFilePreview({ file, url, sizeLabel: formatBytes(file.size) })
        recorder.retake()
        setStep('preview')
      } finally {
        setValidatingFile(false)
      }
    },
    [clearFilePreview, recorder],
  )

  const handleRetake = useCallback(() => {
    clearFilePreview()
    recorder.retake()
    setValidationError(null)
    setStep('capture')
  }, [clearFilePreview, recorder])

  const handleUpload = useCallback(async () => {
    const file = recorder.recording ? recordingToFile(recorder.recording) : filePreview?.file ?? null
    if (!file) return
    setUploadError(null)
    setStep('uploading')
    try {
      // Size guard right before the write as well (never trust earlier state).
      const problem = await validateProofFile(file, { checkDuration: false })
      if (problem) {
        setValidationError(problem)
        setStep('preview')
        return
      }
      const event = await repositories.medication.attachProof(intakeEventId, file)
      setStep('done')
      onUploaded(event)
    } catch (error) {
      setUploadError(errorMessage(error))
      setStep('preview')
    }
  }, [recorder.recording, filePreview, intakeEventId, onUploaded])

  const recorderMessage = recorderPhaseMessage(recorder.phase)
  const previewUrl = recorder.previewUrl ?? filePreview?.url ?? null
  const previewSizeLabel = recorder.recording
    ? `${formatBytes(recorder.recording.blob.size)} · ${formatDuration(recorder.recording.durationSeconds)}`
    : filePreview?.sizeLabel ?? ''

  const body = !PROOF_VIDEO_ENABLED ? (
    <ProofPrivacyGatePanel />
  ) : step === 'consent' ? (
    <ProofConsent onConsent={() => setStep('capture')} />
  ) : step === 'capture' ? (
    <div className="proof-capture">
      {contextLabel ? <p className="proof-text proof-text--muted">{contextLabel}</p> : null}
      {validationError ? <Banner tone="error">{PROOF_VALIDATION_COPY[validationError]}</Banner> : null}
      {recorderMessage ? <Banner tone="warning">{recorderMessage}</Banner> : null}

      {recorder.supported ? (
        <div className="proof-recorder">
          {recorder.phase === 'idle' || recorder.phase === 'requesting' ? (
            <Button
              variant="secondary"
              fullWidth
              loading={recorder.phase === 'requesting'}
              onClick={() => void recorder.requestCamera()}
            >
              <CameraIcon size={18} />
              Camera openen
            </Button>
          ) : null}

          {recorder.phase === 'ready' || recorder.phase === 'recording' ? (
            <div className="proof-live">
              <video
                ref={liveVideoRef}
                className="proof-video"
                muted
                playsInline
                aria-label="Live camerabeeld"
              />
              {recorder.phase === 'recording' ? (
                <p className="proof-timer" role="status" aria-live="polite">
                  Opname bezig · {formatDuration(recorder.elapsedSeconds)} /{' '}
                  {formatDuration(PROOF_MAX_DURATION_SECONDS)}
                </p>
              ) : null}
              {recorder.phase === 'ready' ? (
                <Button variant="primary" fullWidth onClick={recorder.startRecording}>
                  <VideoIcon size={18} />
                  Start opname (max. {PROOF_MAX_DURATION_SECONDS} sec)
                </Button>
              ) : (
                <Button variant="danger" fullWidth onClick={recorder.stopRecording}>
                  Stop opname
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="proof-text">
          Opnemen in de browser wordt op dit apparaat niet ondersteund. Kies hieronder een video
          van je apparaat — op de meeste telefoons opent direct de camera.
        </p>
      )}

      <div className="proof-file-fallback">
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          capture
          className="proof-file-input"
          aria-label="Kies een videobestand"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            void handleFileChosen(event.target.files?.[0] ?? null)
            event.target.value = ''
          }}
        />
        <Button
          variant="ghost"
          fullWidth
          loading={validatingFile}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon size={18} />
          Kies een videobestand
        </Button>
        <p className="proof-text proof-text--muted">
          Maximaal {formatDuration(PROOF_MAX_DURATION_SECONDS)} en 50 MB. De video wordt privé
          opgeslagen en is nooit via een openbare link te zien.
        </p>
      </div>
    </div>
  ) : step === 'preview' ? (
    <div className="proof-preview">
      {uploadError ? <Banner tone="error">{uploadError}</Banner> : null}
      {validationError ? <Banner tone="error">{PROOF_VALIDATION_COPY[validationError]}</Banner> : null}
      {previewUrl ? (
        <video className="proof-video" src={previewUrl} controls playsInline aria-label="Voorbeeld van je opname" />
      ) : null}
      {previewSizeLabel ? <p className="proof-text proof-text--muted">{previewSizeLabel}</p> : null}
      <p className="proof-text">
        Klopt de opname? Je kunt hem hieronder uploaden of opnieuw opnemen. De video is bewijs dat
        jij zelf instuurt — het is geen medische verificatie.
      </p>
      <div className="proof-actions">
        <Button variant="primary" fullWidth onClick={() => void handleUpload()}>
          <UploadIcon size={18} />
          Bewijsvideo uploaden
        </Button>
        <Button variant="secondary" fullWidth onClick={handleRetake}>
          <RefreshIcon size={18} />
          Opnieuw opnemen
        </Button>
      </div>
    </div>
  ) : step === 'uploading' ? (
    <div className="proof-uploading" role="status" aria-live="polite">
      <Spinner size={28} />
      <p className="proof-text">Bewijsvideo wordt privé geüpload…</p>
      <Progress value={0.5} aria-label="Upload voortgang" />
      <p className="proof-text proof-text--muted">Sluit dit scherm niet tijdens het uploaden.</p>
    </div>
  ) : (
    <div className="proof-done" role="status">
      <span className="proof-done-icon" aria-hidden="true">
        <CheckIcon size={24} />
      </span>
      <h3 className="proof-consent-title">Bewijsvideo geüpload</h3>
      <p className="proof-text">
        Je begeleider krijgt een melding en kan de video privé bekijken. De video blijft
        gebruikersbewijs — er wordt nooit automatisch beoordeeld of je medicatie correct is
        ingenomen.
      </p>
      <Button variant="primary" fullWidth onClick={onClose}>
        Sluiten
      </Button>
    </div>
  )

  return (
    <BottomSheet open={open} onClose={onClose} title="Bewijsvideo">
      {body}
    </BottomSheet>
  )
}
